import { createWarehouseSchema, type WarehouseDto } from '@bnn/shared';
import { useState, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { Alert, Badge, Button, Card, Empty, Field, Input, PageHeader, Table } from '../components/ui';
import { api } from '../lib/api';
import { errMsg, useApi } from '../lib/useApi';

export default function WarehousesPage() {
  const { can } = useAuth();
  const manage = can('warehouses.manage');
  const [showInactive, setShowInactive] = useState(false);
  const list = useApi<WarehouseDto[]>(`/warehouses${showInactive ? '?include_inactive=true' : ''}`);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    const parsed = createWarehouseSchema.safeParse({ whs_code: code, whs_name: name });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง');
    setBusy(true);
    try {
      await api.post('/warehouses', parsed.data);
      setCode('');
      setName('');
      list.reload();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const patch = async (w: WarehouseDto, body: Partial<Pick<WarehouseDto, 'whs_name' | 'is_locked' | 'is_active'>>) => {
    setError('');
    try {
      await api.patch(`/warehouses/${encodeURIComponent(w.whs_code)}`, body);
      setEditing(null);
      list.reload();
    } catch (err) {
      setError(errMsg(err));
    }
  };

  return (
    <>
      <PageHeader title="คลังสินค้า" subtitle="เพิ่มและแก้ไขคลังได้ในโปรแกรม · คลังที่ล็อกหรือปิดใช้งานจะสร้างรอบการนับใหม่ไม่ได้" />
      {manage && (
        <Card title="เพิ่มคลัง" className="mb-4">
          <form onSubmit={add} className="grid gap-3 sm:grid-cols-[180px_1fr_auto] sm:items-end">
            <Field label="รหัสคลัง *" hint="A-Z 0-9 - _ . ไม่เกิน 20 ตัว">
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="H01-RM" autoComplete="off" />
            </Field>
            <Field label="ชื่อคลัง *">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="คลังวัตถุดิบ H01" />
            </Field>
            <Button type="submit" variant="primary" loading={busy} className="sm:mb-[18px]">
              + เพิ่มคลัง
            </Button>
          </form>
        </Card>
      )}
      {error && (
        <div className="mb-3">
          <Alert>{error}</Alert>
        </div>
      )}
      <Card
        title={`รายการคลัง${list.data ? ` (${list.data.length})` : ''}`}
        actions={
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> แสดงคลังที่ปิดใช้งาน
          </label>
        }
      >
        {list.error && <Alert>{list.error.message}</Alert>}
        {list.data && list.data.length === 0 ? (
          <Empty>ยังไม่มีคลัง{manage ? ' — เพิ่มด้านบน' : ''}</Empty>
        ) : (
          <Table head={['รหัส', 'ชื่อคลัง', 'สถานะ', ...(manage ? [''] : [])]} minWidth={manage ? 620 : 420}>
            {list.data?.map((w) => (
              <tr key={w.whs_code} className={w.is_active ? '' : 'text-muted'}>
                <td className="font-semibold whitespace-nowrap">{w.whs_code}</td>
                <td>
                  {editing === w.whs_code ? (
                    <form
                      className="flex gap-2"
                      onSubmit={(e) => {
                        e.preventDefault();
                        void patch(w, { whs_name: editName });
                      }}
                    >
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus className="py-1.5" />
                      <Button size="sm" type="submit" variant="primary">
                        บันทึก
                      </Button>
                      <Button size="sm" type="button" onClick={() => setEditing(null)}>
                        ยกเลิก
                      </Button>
                    </form>
                  ) : (
                    w.whs_name
                  )}
                </td>
                <td className="space-x-1 whitespace-nowrap">
                  {!w.is_active ? <Badge>ปิดใช้งาน</Badge> : w.is_locked ? <Badge tone="warn">ล็อก</Badge> : <Badge tone="ok">ใช้งาน</Badge>}
                </td>
                {manage && (
                  <td className="space-x-1 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      onClick={() => {
                        setEditing(w.whs_code);
                        setEditName(w.whs_name);
                      }}
                    >
                      แก้ชื่อ
                    </Button>
                    {w.is_active && (
                      <Button size="sm" onClick={() => void patch(w, { is_locked: !w.is_locked })}>
                        {w.is_locked ? 'ปลดล็อก' : 'ล็อก'}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant={w.is_active ? 'danger' : 'secondary'}
                      onClick={() => {
                        if (!w.is_active || confirm(`ปิดใช้งานคลัง ${w.whs_code}? (ประวัติการนับยังอยู่ครบ)`)) void patch(w, { is_active: !w.is_active });
                      }}
                    >
                      {w.is_active ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}
